import React, { useMemo, useState } from 'react';
import Select, { components } from 'react-select';
import logo from '../../../img/logo.svg';
import './other-reports.css';
import './report-table.css';
import {
  applyReportEndDateChange,
  applyReportStartDateChange,
  getReportDateRangeBounds,
  todayIso,
} from '../../../utils/reportDateRange';

const reportSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    height: 36,
    borderColor: state.isFocused ? '#0078d4' : '#8a8886',
    boxShadow: state.isFocused ? '0 0 0 1px #0078d4' : 'none',
    borderRadius: 4,
    fontSize: '0.9375rem',
    fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    height: 34,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 40,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const labelFromChildren = (children) => {
  if (children == null || children === false) {
    return '';
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(labelFromChildren).join('');
  }
  if (React.isValidElement(children)) {
    return labelFromChildren(children.props.children);
  }
  return '';
};

const optionsFromChildren = (children) => {
  const options = [];
  React.Children.forEach(children, (child) => {
    if (!child || child.type !== 'option') {
      return;
    }
    options.push({
      value: child.props.value == null ? '' : String(child.props.value),
      label: labelFromChildren(child.props.children),
    });
  });
  return options;
};

const normalizeSelectOptions = (options = []) =>
  options
    .filter((item) => item != null)
    .map((item) => ({
      value: item.value == null ? String(item.id ?? '') : String(item.value),
      label: String(item.label ?? item.name ?? item.accounts_head ?? ''),
    }))
    .filter((item) => item.value !== '' || item.label);

export const OtherReportShell = ({ children, className = '' }) => (
  <div className={`other-report-shell ${className}`.trim()}>{children}</div>
);

export const OtherReportPageHeader = ({ title, subtitle, badge = 'Other Reports' }) => (
  <div className="other-report-page-header">
    <div className="other-report-page-header-main">
      <span className="other-report-page-badge">{badge}</span>
      <h1>{title}</h1>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  </div>
);

/** @deprecated use OtherReportPageHeader */
export const OtherReportPageTitle = OtherReportPageHeader;

export const OtherReportFilterCard = ({
  title = 'Report parameters',
  description = 'Set the date range and filters, then run or export the report.',
  className = '',
  children,
}) => (
  <div className={`other-report-filter-card ${className}`.trim()}>
    <div className="other-report-card-header">
      <div className="other-report-card-header-icon" aria-hidden="true">⚙</div>
      <div className="other-report-card-header-text">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
    <div className="other-report-card-body">{children}</div>
  </div>
);

export const OtherReportToolbar = ({ dates, hint, actions, hintWide = false, actionsWide = false }) => (
  <div className="other-report-toolbar-grid">
    {dates}
    {hint ? (
      <div className={`other-report-toolbar-hint${hintWide ? ' other-report-toolbar-hint--wide' : ''}`}>
        {hint}
      </div>
    ) : null}
    <div className={`other-report-toolbar-actions${actionsWide || !hint ? ' other-report-toolbar-actions--wide' : ''}`}>
      {actions}
    </div>
  </div>
);

export const OtherReportDateField = ({
  label,
  value,
  onChange,
  min,
  max,
  span = 2,
}) => (
  <div className={`other-report-field${span !== 2 ? ` other-report-field--span-${span}` : ''}`}>
    <label>{label}</label>
    <input
      type="date"
      className="form-control"
      value={value || ''}
      min={min || undefined}
      max={max || undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/** Start/end date pair with shared bounds and auto-sync when end date changes. */
export const ReportDateRangeFields = ({
  startDate,
  endDate,
  onChange,
  maxDateRangeDays = null,
  today,
  startLabel = 'Start date',
  endLabel = 'End date',
}) => {
  const todayValue = today || todayIso();
  const bounds = useMemo(
    () => getReportDateRangeBounds({
      startDate,
      endDate,
      maxDateRangeDays,
      today: todayValue,
    }),
    [startDate, endDate, maxDateRangeDays, todayValue],
  );

  const rangeOptions = { maxDateRangeDays, today: todayValue };

  const handleStart = (value) => {
    onChange(applyReportStartDateChange({ start_date: startDate, end_date: endDate }, value, rangeOptions));
  };

  const handleEnd = (value) => {
    onChange(applyReportEndDateChange({ start_date: startDate, end_date: endDate }, value, rangeOptions));
  };

  return (
    <>
      <OtherReportDateField
        label={startLabel}
        value={startDate}
        min={bounds.startMin || undefined}
        max={bounds.startMax || undefined}
        onChange={handleStart}
      />
      <OtherReportDateField
        label={endLabel}
        value={endDate}
        min={bounds.endMin || undefined}
        max={bounds.endMax || undefined}
        onChange={handleEnd}
      />
    </>
  );
};

/**
 * Searchable account/customer/head dropdown (react-select), matching legacy Laravel reports.
 * Accepts either `options` [{ value|id, label|name|accounts_head }] or native <option> children.
 */
export const OtherReportSelectField = ({
  label,
  value,
  onChange,
  children,
  options,
  placeholder = 'Select…',
  span = 2,
  isClearable = true,
  isSearchable = true,
  className = '',
}) => {
  const selectOptions = useMemo(() => {
    if (Array.isArray(options) && options.length) {
      return normalizeSelectOptions(options);
    }
    return optionsFromChildren(children);
  }, [options, children]);

  const selected =
    selectOptions.find((opt) => String(opt.value) === String(value ?? '')) || null;

  return (
    <div
      className={`other-report-field other-report-field--select${
        span !== 2 ? ` other-report-field--span-${span}` : ''
      } ${className}`.trim()}
    >
      {label ? <label>{label}</label> : null}
      <Select
        classNamePrefix="other-report-select"
        styles={reportSelectStyles}
        options={selectOptions}
        value={selected}
        onChange={(opt) => onChange(opt ? String(opt.value) : '')}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable={isSearchable}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        noOptionsMessage={() => 'No matches'}
      />
    </div>
  );
};

/**
 * Searchable multi-select with Select all / Clear all (legacy Laravel multiselect parity).
 * `value` is an array of option values; `onChange` receives that array.
 * Many selections collapse to a count summary so the filter card does not grow unbounded.
 */
export const OtherReportMultiSelectField = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = 'Select…',
  span = 3,
  selectAllLabel = 'Select all',
  unselectAllLabel = 'Unselect all',
  className = '',
  /** Show individual chips only up to this count; beyond that, show "N selected". */
  chipLimit = 2,
  itemLabel = 'selected',
}) => {
  const selectOptions = useMemo(
    () => normalizeSelectOptions(options),
    [options]
  );

  const selectedValues = useMemo(
    () => (Array.isArray(value) ? value.map((item) => String(item)) : []),
    [value]
  );

  const selected = useMemo(
    () => selectOptions.filter((opt) => selectedValues.includes(String(opt.value))),
    [selectOptions, selectedValues]
  );

  const allSelected =
    selectOptions.length > 0 && selected.length === selectOptions.length;

  const CompactMultiValue = useMemo(() => {
    const MultiValue = (props) => {
      const values = props.getValue() || [];
      if (values.length > chipLimit) {
        if (props.index !== 0) {
          return null;
        }
        const summary = allSelected
          ? `All ${values.length} ${itemLabel}`
          : `${values.length} ${itemLabel}`;
        return (
          <div className="other-report-multi-summary" title={values.map((opt) => opt.label).join(', ')}>
            {summary}
          </div>
        );
      }
      return <components.MultiValue {...props} />;
    };
    MultiValue.displayName = 'OtherReportCompactMultiValue';
    return MultiValue;
  }, [allSelected, chipLimit, itemLabel]);

  return (
    <div
      className={`other-report-field other-report-field--select other-report-field--multi${
        span !== 2 ? ` other-report-field--span-${span}` : ''
      } ${className}`.trim()}
    >
      <div className="other-report-multi-label-row">
        {label ? <label>{label}</label> : <span />}
        {selectOptions.length > 0 ? (
          <div className="other-report-multi-actions">
            <button
              type="button"
              className="other-report-select-all-btn"
              disabled={allSelected}
              onClick={() => onChange(selectOptions.map((opt) => opt.value))}
            >
              {selectAllLabel}
            </button>
            <button
              type="button"
              className="other-report-select-all-btn"
              disabled={selected.length === 0}
              onClick={() => onChange([])}
            >
              {unselectAllLabel}
            </button>
          </div>
        ) : null}
      </div>
      <Select
        classNamePrefix="other-report-select"
        styles={{
          ...reportSelectStyles,
          control: (base, state) => ({
            ...reportSelectStyles.control(base, state),
            height: 'auto',
            minHeight: 36,
            maxHeight: 76,
          }),
          valueContainer: (base) => ({
            ...base,
            padding: '2px 8px',
            height: 'auto',
            maxHeight: 70,
            overflowY: 'auto',
            flexWrap: 'wrap',
          }),
          multiValue: (base) => ({
            ...base,
            maxWidth: '100%',
          }),
          multiValueLabel: (base) => ({
            ...base,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 180,
          }),
          indicatorsContainer: (base) => ({
            ...base,
            height: 'auto',
            alignSelf: 'stretch',
          }),
        }}
        components={{ MultiValue: CompactMultiValue }}
        options={selectOptions}
        value={selected}
        onChange={(opts) => onChange((opts || []).map((opt) => String(opt.value)))}
        placeholder={placeholder}
        isMulti
        isClearable
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        noOptionsMessage={() => 'No matches'}
      />
    </div>
  );
};

/**
 * Checkbox multi-select with search plus Select all / Unselect all.
 * Prefer `onChange(ids)` for bulk updates; `onToggle(id)` still works for single clicks.
 */
export const OtherReportSearchableCheckList = ({
  items = [],
  selectedIds = [],
  onToggle,
  onChange,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  selectAllLabel = 'Select all',
  unselectAllLabel = 'Unselect all',
  getId = (item) => item.id,
  getLabel = (item) => item.label || item.name || item.product_name || item.accounts_head || '',
}) => {
  const [query, setQuery] = useState('');
  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter((item) => String(getLabel(item)).toLowerCase().includes(q));
  }, [items, query, getLabel]);

  const filteredIds = useMemo(
    () => filtered.map((item) => String(getId(item))),
    [filtered, getId]
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedSet.has(id));

  const applySelection = (ids) => {
    if (typeof onChange === 'function') {
      onChange(ids.map(String));
    }
  };

  const handleSelectAll = () => {
    // Select currently visible (filtered) items; keep other selections.
    const next = new Set(selectedSet);
    filteredIds.forEach((id) => next.add(id));
    applySelection(Array.from(next));
  };

  const handleUnselectAll = () => {
    if (query.trim()) {
      // Unselect only visible filtered items when searching.
      const remove = new Set(filteredIds);
      applySelection(Array.from(selectedSet).filter((id) => !remove.has(id)));
      return;
    }
    applySelection([]);
  };

  return (
    <div className="other-report-searchable-check-list">
      <input
        type="search"
        className="form-control other-report-check-search"
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={searchPlaceholder}
      />
      {items.length > 0 ? (
        <div className="other-report-check-list-toolbar">
          <span className="other-report-check-list-count">
            {filteredIds.length === items.length
              ? `${items.length} item${items.length === 1 ? '' : 's'}`
              : `${filteredIds.length} of ${items.length}`}
            {selectedSet.size > 0 ? ` · ${selectedSet.size} selected` : ''}
          </span>
          <div className="other-report-multi-actions">
            <button
              type="button"
              className="other-report-select-all-btn"
              disabled={allFilteredSelected || filteredIds.length === 0}
              onClick={handleSelectAll}
            >
              {selectAllLabel}
            </button>
            <button
              type="button"
              className="other-report-select-all-btn"
              disabled={selectedSet.size === 0 || filteredIds.length === 0}
              onClick={handleUnselectAll}
            >
              {unselectAllLabel}
            </button>
          </div>
        </div>
      ) : null}
      <div className="other-report-checkbox-panel">
        {filtered.map((item) => {
          const id = String(getId(item));
          return (
            <label key={id} className="d-block mb-1">
              <input
                type="checkbox"
                className="form-check-input me-2"
                checked={selectedSet.has(id)}
                onChange={() => {
                  if (typeof onToggle === 'function') {
                    onToggle(getId(item));
                    return;
                  }
                  const next = new Set(selectedSet);
                  if (next.has(id)) {
                    next.delete(id);
                  } else {
                    next.add(id);
                  }
                  applySelection(Array.from(next));
                }}
              />
              {getLabel(item)}
            </label>
          );
        })}
        {!filtered.length ? (
          <p className="mb-0 small text-muted">{emptyMessage}</p>
        ) : null}
      </div>
    </div>
  );
};

export const OtherReportCheckboxField = ({
  label,
  checked,
  onChange,
  id,
}) => (
  <div className="other-report-field">
    <label htmlFor={id}>{label}</label>
    <div className="other-report-checkbox-inline">
      <input
        type="checkbox"
        className="form-check-input"
        id={id}
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  </div>
);

/** Empty table cells used to pad opening-balance rows (legacy report alignment). */
export const OtherReportEmptyCells = ({ count = 1 }) =>
  Array.from({ length: count }, (_, index) => (
    <td key={`empty-${index}`} aria-hidden="true" />
  ));

/** Single primary action — same size on every report page */
export const ReportSubmitButton = ({
  loading = false,
  disabled = false,
  onClick,
  label = 'Run report',
}) => (
  <button
    type="button"
    className="other-report-btn other-report-btn-primary"
    onClick={onClick}
    disabled={disabled || loading}
  >
    {loading ? 'Running…' : label}
  </button>
);

export const OtherReportActionButtons = ({
  loading,
  pdfLoading,
  onSubmit,
  onDownloadPdf,
  submitLabel = 'Run report',
  pdfLabel = 'Download PDF',
}) => (
  <div className="other-report-actions">
    <button
      type="button"
      className="other-report-btn other-report-btn-primary"
      onClick={onSubmit}
      disabled={loading || pdfLoading}
    >
      {loading ? 'Running…' : submitLabel}
    </button>
    <button
      type="button"
      className="other-report-btn other-report-btn-secondary"
      onClick={onDownloadPdf}
      disabled={loading || pdfLoading}
    >
      {pdfLoading ? 'Exporting…' : pdfLabel}
    </button>
  </div>
);

export const OtherReportFilterSection = ({ title, description, children }) => (
  <div className="other-report-filter-section">
    <div className="other-report-filter-section-head">
      <span className="other-report-filter-section-title">{title}</span>
      {description ? (
        <span className="other-report-filter-section-desc">{description}</span>
      ) : null}
    </div>
    {children}
  </div>
);

export const OtherReportGroupingToolbar = ({
  modes = [],
  value = 'none',
  onChange,
  label = 'Group by',
}) => {
  if (!modes.length) {
    return null;
  }

  return (
    <div className="other-report-grouping-toolbar" role="group" aria-label={label}>
      <span className="other-report-grouping-label">{label}</span>
      {modes.map((mode) => (
        <button
          key={mode.id}
          type="button"
          className={`other-report-grouping-btn${value === mode.id ? ' is-active' : ''}`}
          onClick={() => onChange?.(mode.id)}
        >
          {mode.label}
        </button>
      ))}
    </div>
  );
};

export const OtherReportResultsZone = ({ children }) => (
  <div className="other-report-results-zone">{children}</div>
);

export const OtherReportPlaceholder = ({
  title = 'Ready to generate report',
  message = 'Choose your date range and any filters above, then click Run report to view results here.',
}) => (
  <div className="other-report-placeholder">
    <div className="other-report-placeholder-icon" aria-hidden="true">📊</div>
    <h3>{title}</h3>
    <p>{message}</p>
  </div>
);

export const OtherReportLoader = ({ message = 'Generating report' }) => (
  <div className="other-report-loader" role="status" aria-live="polite">
    <div className="other-report-loader-ring" aria-hidden="true" />
    <div className="other-report-loader-text">
      {message}
      <span className="other-report-loader-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
    </div>
  </div>
);

export const OtherReportDocument = ({
  title,
  subtitle,
  meta,
  rowCount,
  children,
  note,
  logoPosition = 'side',
}) => (
  <div className={`other-report-document${logoPosition === 'top' ? ' other-report-document--brand-top' : ''}`}>
    {logoPosition === 'top' ? (
      <div className="other-report-document-brand">
        <img src={logo} alt="Petrosmart" className="other-report-document-logo" />
      </div>
    ) : null}
    <div className="other-report-document-header">
      <div className="other-report-document-header-main">
        {subtitle ? <h2>{subtitle}</h2> : null}
        <h3>{title}</h3>
        <div className="other-report-document-meta-row">
          {meta ? <p className="other-report-document-meta">{meta}</p> : null}
          {rowCount != null && rowCount >= 0 ? (
            <span className="other-report-badge other-report-badge--primary">
              {rowCount} {rowCount === 1 ? 'record' : 'records'}
            </span>
          ) : null}
        </div>
      </div>
      {logoPosition !== 'top' ? (
        <img src={logo} alt="Petrosmart" className="other-report-document-logo" />
      ) : null}
    </div>
    {note ? <div className="other-report-note">{note}</div> : null}
    <div className="other-report-document-body">{children}</div>
  </div>
);

export const OtherReportSummaryGrid = ({ items }) => {
  if (!items?.length) {
    return null;
  }

  return (
    <div className="other-report-summary-grid">
      {items.map((item, index) => (
        <div className="other-report-summary-item" key={`summary-${index}`}>
          <strong>{item.label}</strong>
          {item.value}
        </div>
      ))}
    </div>
  );
};

export const OtherReportTable = ({
  columns,
  children,
  className = '',
  footer,
}) => (
  <>
    <div className="other-report-table-wrap">
      <table className={`other-report-table ${className}`.trim()}>
        {columns ? (
          <>
            <colgroup>
              {columns.map((column) => (
                <col
                  key={`col-${column.key || column}`}
                  className={column.colClass || undefined}
                  style={column.width ? { width: column.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column) => {
                  const isRight = column.align === 'right';
                  return (
                    <th
                      key={column.key || column}
                      className={[
                        isRight ? 'text-end' : '',
                        isRight ? 'report-amount-header' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      {column.label || column}
                    </th>
                  );
                })}
              </tr>
            </thead>
          </>
        ) : null}
        {children}
      </table>
    </div>
    {footer ? <div className="other-report-table-footer">{footer}</div> : null}
  </>
);

export const OtherReportEmpty = ({
  message = 'No data found for the selected filters.',
  hint = 'Try widening the date range or changing filter options.',
}) => (
  <div className="other-report-empty">
    <div className="other-report-empty-icon" aria-hidden="true">—</div>
    <p className="mb-1 fw-semibold text-secondary">{message}</p>
    {hint ? <p className="mb-0 small">{hint}</p> : null}
  </div>
);

export const OtherReportSectionBlock = ({ title, children }) => (
  <div className="other-report-section-block">
    {title ? <h4 className="other-report-section-title">{title}</h4> : null}
    {children}
  </div>
);
